import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ShieldCheck,
  Vote,
  ScrollText,
  Users,
  Building2,
  Gavel,
  ChevronDown,
  Upload,
  ArrowRight,
  CheckCircle2,
  Lock,
  Landmark,
  Mic,
  BookOpen,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_fe552c1f-50e5-44f8-b8e8-46eba2b43771/artifacts/wqej773k_Queen-City-Condo-Association.png";

const BOARD_POSITIONS = [
  { value: "president", label: "President" },
  { value: "vice_president", label: "Vice President" },
  { value: "secretary", label: "Secretary" },
  { value: "treasurer", label: "Treasurer" },
  { value: "member_at_large", label: "Member at Large" },
  { value: "other", label: "Other" },
];

const WHY_CARDS = [
  {
    icon: ShieldCheck,
    title: "Governance Without Gatekeepers",
    body:
      "Reclaim control from property management firms that prioritize their convenience over your rights. A board-only space to share the legal strategies and precedents needed to govern autonomously.",
  },
  {
    icon: Users,
    title: "A Voting Bloc for the City",
    body:
      "Regina's condo community is large, significant, and ready to be heard. By connecting boards across the city we create a network capable of influencing municipal policy.",
  },
  {
    icon: Gavel,
    title: "A Decision Engine for Every Building",
    body:
      "Secure digital voting, audited decision logs, and a template vault — the infrastructure every board needs to do its job without outsourcing legal interpretation.",
  },
];

const PLATFORM_CARDS = [
  {
    icon: Vote,
    title: "Secure Vote-by-Email",
    status: "Live",
    body:
      "Upload your unit-owner CSV and we generate a unique tokenized ballot for every owner. Track turnout in real time, record an audited decision log, and publish transparent results — without renting a ballot box. Perfect for AGM minute waivers, audit waivers, and bylaw amendments.",
  },
  {
    icon: Mic,
    title: "Digital Clerk",
    status: "Live",
    body:
      "Record your board meeting in-browser (or upload an audio file), and the Digital Clerk transcribes and structures it into proper meeting minutes — attendees, motions (with carried / not-carried), action items with owners and due dates, and decisions. Editable before you save it to the Minute Book.",
  },
  {
    icon: BookOpen,
    title: "Digital Minute Book",
    status: "Live",
    body:
      "A tidy, searchable minute book for every meeting — auto-filed from the Digital Clerk or uploaded manually. Email copies to the board with one click. Every page is time-stamped and attributed.",
  },
  {
    icon: ScrollText,
    title: "Template Vault",
    status: "Coming soon",
    body:
      "Meeting minutes, civil-enforcement packages, bylaw amendment templates, and correspondence drafts — peer-reviewed and ready to adapt. Save hours of legal-adjacent work every month.",
  },
  {
    icon: Landmark,
    title: "Director Forum",
    status: "Coming soon",
    body:
      "A candid board-only discussion board for the issues that actually matter — legal strategy, precedent, and the structural future of Regina's condo stock. Moderated by verified directors only.",
  },
  {
    icon: Users,
    title: "Live Board Room",
    status: "Coming soon",
    body:
      "Verified boards can meet live when they need to — emergency quorums, inter-corp strategy sessions, or just to compare notes. Encrypted, with recordings that flow directly into the Digital Clerk.",
  },
  {
    icon: ShieldCheck,
    title: "Property Manager Ratings",
    status: "Coming soon",
    body:
      "Crowdsourced, verified-director-only ratings of property management firms and service providers. If a firm isn't serving Regina's condo owners with integrity, the network knows.",
  },
  {
    icon: Gavel,
    title: "City Policy Alerts",
    status: "Coming soon",
    body:
      "Automated alerts the moment the City of Regina puts anything on its agenda that touches condo properties — taxation, zoning, waste, building-code changes. Never be the last to hear again.",
  },
];

const FAQS = [
  {
    id: "eligibility",
    q: "Who is eligible to join QCCA?",
    a: "Only elected Directors of a registered condominium corporation in Regina. That means Presidents, VPs, Secretaries, Treasurers, and Directors at Large. No property managers, no third-party firms, no bureaucrats.",
  },
  {
    id: "minutes",
    q: "Why do you require AGM minutes or a signature page?",
    a: "Verification protects the integrity of the network. A recent AGM minutes excerpt or elected-directors signature page confirms you currently hold a board seat. Documents are handled confidentially and used only for verification.",
  },
  {
    id: "cost",
    q: "What does membership cost?",
    a: "Your first year is free — no credit card required to apply. After that, the base tier is $79.99 per board per year, which covers the President and the Secretary. Each additional director gets a seat for $19.99 per year with full access to the Minute Book, emailed minutes, and every member tool. Your first secure vote is included at no charge; additional votes for larger corporations are billed per-ballot at a rate designed to be far cheaper than hiring a ballot firm.",
  },
  {
    id: "affiliation",
    q: "Is QCCA affiliated with the City of Regina or any political party?",
    a: "No. QCCA is independent and non-partisan. Our mandate is to represent Regina's condo owners to every level of government — whoever is at the table.",
  },
  {
    id: "verification",
    q: "How long does verification take?",
    a: "Most applications are reviewed within 3–5 business days. If a verifier needs more information we'll reach out directly; otherwise you'll receive your access credentials and director badge by email.",
  },
];

const fadeStyle = (delayMs) => ({
  animationDelay: `${delayMs}ms`,
});

export default function QCCALanding() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {application_id, status}
  const [openFaq, setOpenFaq] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    condo_corporation_name: "",
    building_address: "",
    board_position: "",
    years_on_board: "",
    message: "",
    terms_accepted: false,
  });
  const [agmFile, setAgmFile] = useState(null);
  const fileRef = useRef(null);
  const applyRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/qcca/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const scrollToApply = () => {
    applyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setField = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const canSubmit = useMemo(() => {
    return (
      form.full_name.trim() &&
      /.+@.+\..+/.test(form.email) &&
      form.condo_corporation_name.trim() &&
      form.building_address.trim() &&
      form.board_position &&
      form.terms_accepted &&
      !submitting
    );
  }, [form, submitting]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setAgmFile(null);
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB).");
      e.target.value = "";
      return;
    }
    setAgmFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
      if (agmFile) fd.append("agm_document", agmFile);

      const res = await axios.post(`${API}/qcca/applications`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted({
        application_id: res.data.application_id,
        status: res.data.status,
      });
      toast.success("Application submitted. Welcome to the movement.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        condo_corporation_name: "",
        building_address: "",
        board_position: "",
        years_on_board: "",
        message: "",
        terms_accepted: false,
      });
      setAgmFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative z-10">
      {/* ===================== NAV ===================== */}
      <nav
        className="sticky top-0 z-40 bg-black/80 backdrop-blur-md border-b border-[#1a1a1a]"
        data-testid="qcca-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-mono text-xs text-gray-400 hover:text-[#C5A52A] transition-colors flex items-center gap-2">
              ← Back to CYQR.CA
            </a>
            <div className="w-px h-6 bg-[#1a1a1a] hidden sm:block" />
            <a href="#top" className="flex items-center gap-3" data-testid="qcca-nav-logo">
              <img src={LOGO_URL} alt="QCCA" className="h-10 w-10 rounded-full bg-[#0a0a0a] p-0.5 border border-[#1a1a1a]" />
              <div className="hidden sm:block">
                <div className="font-headline text-sm tracking-wide text-white leading-none">Queen City Condo Association</div>
                <div className="font-mono text-[10px] text-[#C5A52A] mt-1 tracking-[0.2em] uppercase">Regina · Est. 2026</div>
              </div>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.18em] text-gray-400">
            <a href="#why" className="hover:text-white transition-colors">Why</a>
            <a href="#platform" className="hover:text-white transition-colors">Toolkit</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <a href="/qcca/boardroom/login" className="hover:text-[#C5A52A] transition-colors" data-testid="qcca-nav-boardroom-link">Boardroom →</a>
          </div>
          <button
            onClick={scrollToApply}
            data-testid="qcca-nav-apply-btn"
            className="group inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
          >
            Apply <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </nav>

      {/* ===================== HERO ===================== */}
      <section id="top" className="relative overflow-hidden border-b border-[#1a1a1a]">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.09]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 10%, #C5A52A 0, transparent 40%), radial-gradient(circle at 80% 80%, #1e3a5f 0, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-36 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="qcca-in" style={fadeStyle(0)}>
              <div className="inline-flex items-center gap-2 border border-[#C5A52A]/40 text-[#C5A52A] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5">
                <Lock className="w-3 h-3" /> Gated · Board Directors Only
              </div>
            </div>
            <h1
              className="qcca-in mt-6 font-headline text-white tracking-tight leading-[0.95]"
              style={fadeStyle(100)}
              data-testid="qcca-hero-title"
            >
              <span className="block text-5xl sm:text-6xl lg:text-7xl font-medium">Thousands of Doors.</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl italic text-[#C5A52A] font-normal">One Unified Voice.</span>
            </h1>
            <p
              className="qcca-in mt-8 max-w-xl text-lg text-gray-300 leading-relaxed"
              style={fadeStyle(220)}
            >
              Regina condo owners represent a massive, untapped political force. The <span className="text-white font-medium">Queen City Condo Association</span> is the first professional, non-partisan network of elected Board Directors across the city — built to make sure that when Regina's condo owners speak, <span className="text-[#C5A52A]">the city listens.</span>
            </p>
            <div className="qcca-in mt-10 flex flex-wrap items-center gap-4" style={fadeStyle(340)}>
              <button
                onClick={scrollToApply}
                data-testid="qcca-hero-apply-btn"
                className="qcca-gold-glow group inline-flex items-center gap-3 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-6 py-4 transition-colors"
              >
                Apply for Board Membership
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#why"
                data-testid="qcca-hero-learn-btn"
                className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-xs uppercase tracking-[0.22em] px-6 py-4 transition-colors"
              >
                Learn the mandate
              </a>
            </div>
            {stats && (
              <div
                className="qcca-in mt-14 grid grid-cols-3 gap-6 max-w-lg"
                style={fadeStyle(460)}
                data-testid="qcca-hero-stats"
              >
                {[
                  { k: "applications", label: "Applications", v: stats.total_applications ?? 0 },
                  { k: "directors", label: "Verified directors", v: stats.approved_directors ?? 0 },
                  { k: "corps", label: "Corporations", v: stats.corporations_represented ?? 0 },
                ].map((s) => (
                  <div key={s.k} className="border-l border-[#C5A52A]/50 pl-4">
                    <div className="font-headline text-3xl text-white">{s.v}</div>
                    <div className="font-mono text-[10px] text-gray-400 uppercase tracking-[0.18em] mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative qcca-in" style={fadeStyle(160)}>
              <div className="absolute -inset-8 rounded-full bg-[#C5A52A]/10 blur-3xl" />
              <img
                src={LOGO_URL}
                alt="Queen City Condo Association seal"
                className="relative w-[260px] sm:w-[340px] lg:w-[420px] drop-shadow-[0_30px_60px_rgba(197,165,42,0.25)]"
                data-testid="qcca-hero-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY ===================== */}
      <section id="why" className="py-24 lg:py-32 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">01 · The Mandate</div>
              <h2 className="mt-4 font-headline text-4xl lg:text-5xl text-white leading-tight">
                Why we are <span className="italic text-[#C5A52A]">organizing.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-8">
              <p className="text-gray-400 text-lg leading-relaxed">
                Every day, thousands of us in Regina navigate condo governance, deal with property management gatekeeping, and manage the infrastructure of our city's densest neighborhoods. Yet when it comes to City Hall, our voices are fragmented. QCCA exists to change that — permanently.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#1a1a1a] border border-[#1a1a1a]">
            {WHY_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="group bg-black p-8 hover:bg-[#0a0a0a] transition-colors"
                  data-testid={`qcca-why-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-[#C5A52A]/40 text-[#C5A52A] group-hover:bg-[#C5A52A] group-hover:text-black transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-6 font-headline text-2xl text-white leading-tight">{c.title}</h3>
                  <p className="mt-4 text-gray-400 leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== PLATFORM / YOUR TOOLKIT ===================== */}
      <section id="platform" className="py-24 lg:py-32 border-b border-[#1a1a1a] bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">02 · Your Toolkit</div>
              <h2 className="mt-4 font-headline text-4xl lg:text-5xl text-white leading-tight">
                What every verified director <span className="italic text-[#C5A52A]">gets.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-8">
              <p className="text-gray-400 text-lg leading-relaxed">
                Six integrated tools built specifically for elected directors — with verification gating, audit trails, and member control at every layer. Secure vote-by-email is <span className="text-white">live now</span>; the rest roll out across 2026.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLATFORM_CARDS.map((c, i) => {
              const Icon = c.icon;
              const isLive = c.status === "Live";
              return (
                <article
                  key={c.title}
                  className={`relative bg-[#0a0a0a] border p-8 transition-colors ${
                    isLive ? "border-[#C5A52A]/60" : "border-[#1a1a1a] hover:border-[#C5A52A]/40"
                  }`}
                  data-testid={`qcca-platform-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <Icon className="w-7 h-7 text-[#C5A52A]" />
                    <span
                      className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border ${
                        isLive ? "border-[#C5A52A] text-[#C5A52A] bg-[#C5A52A]/5" : "border-[#1a1a1a] text-gray-500"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <h3 className="mt-6 font-headline text-2xl text-white leading-tight">{c.title}</h3>
                  <p className="mt-4 text-gray-400 leading-relaxed">{c.body}</p>
                </article>
              );
            })}
          </div>

          {/* Pricing callout */}
          <div
            className="mt-14 border border-[#C5A52A]/40 bg-[#0a0a0a] p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center"
            data-testid="qcca-pricing-callout"
          >
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Introductory Pricing</div>
              <h3 className="mt-3 font-headline text-3xl text-white leading-tight">
                Your first year is <span className="italic text-[#C5A52A]">on us.</span>
              </h3>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Full access to the toolkit for 12 months — no credit card required to apply. After that, the base tier is <span className="text-white">$79.99 / board / year</span> (covers the <span className="text-white">President</span> and <span className="text-white">Secretary</span>). Each additional board director gets their own seat for <span className="text-white">$19.99 / year</span> — with access to the Minute Book, emailed minutes, and every member tool. Your first secure vote is also included at no charge.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              <div className="border border-[#1a1a1a] p-5 bg-black col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Year one</div>
                <div className="mt-2 font-headline text-4xl text-[#C5A52A]">Free</div>
              </div>
              <div className="border border-[#1a1a1a] p-5 bg-black">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Base tier</div>
                <div className="mt-2 font-headline text-3xl text-white">$79.99<span className="text-sm text-gray-500">/yr</span></div>
                <div className="mt-1 font-mono text-[10px] text-gray-500">President + Secretary</div>
              </div>
              <div className="border border-[#1a1a1a] p-5 bg-black">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Each add'l director</div>
                <div className="mt-2 font-headline text-3xl text-white">$19.99<span className="text-sm text-gray-500">/yr</span></div>
                <div className="mt-1 font-mono text-[10px] text-gray-500">Full toolkit access</div>
              </div>
              <div className="border border-[#1a1a1a] p-5 bg-black col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">First secure vote</div>
                <div className="mt-2 font-headline text-2xl text-[#C5A52A]">Included, no charge</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== APPLY ===================== */}
      <section
        id="apply"
        ref={applyRef}
        className="py-24 lg:py-32 border-b border-[#1a1a1a]"
        data-testid="qcca-apply-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">03 · Join the Movement</div>
            <h2 className="mt-4 font-headline text-4xl lg:text-5xl text-white">
              If you serve on a Condo Board,
              <br />
              <span className="italic text-[#C5A52A]">you have a seat at the table.</span>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-gray-400 leading-relaxed">
              Verification required: please have your most recent AGM minutes excerpt or elected-directors signature page ready to upload. No property managers, no third-party firms, no bureaucrats.
            </p>
          </div>

          {submitted ? (
            <div
              className="bg-[#0a0a0a] border border-[#C5A52A]/50 p-10 text-center"
              data-testid="qcca-application-success"
            >
              <CheckCircle2 className="w-14 h-14 text-[#C5A52A] mx-auto" />
              <h3 className="mt-6 font-headline text-3xl text-white">Application received.</h3>
              <p className="mt-3 text-gray-400">
                Reference ID:{" "}
                <span className="font-mono text-[#C5A52A]">{submitted.application_id}</span>
              </p>
              <p className="mt-6 max-w-xl mx-auto text-gray-400 leading-relaxed">
                A QCCA verifier will review your submission within 3–5 business days. Once approved, your director badge will be issued and the member platform unlocked. Keep an eye on your inbox — and your spam folder, just in case.
              </p>
              <button
                onClick={() => setSubmitted(null)}
                className="mt-8 font-mono text-xs uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white transition-colors"
                data-testid="qcca-application-success-reset"
              >
                Submit another → 
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 sm:p-10 space-y-6"
              data-testid="qcca-application-form"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Full name *" testId="qcca-field-full_name">
                  <input
                    required
                    value={form.full_name}
                    onChange={setField("full_name")}
                    className={inputCls}
                    placeholder="Jane Director"
                    data-testid="qcca-input-full_name"
                  />
                </Field>
                <Field label="Email *" testId="qcca-field-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    className={inputCls}
                    placeholder="director@corporation.ca"
                    data-testid="qcca-input-email"
                  />
                </Field>
                <Field label="Phone (optional)" testId="qcca-field-phone">
                  <input
                    value={form.phone}
                    onChange={setField("phone")}
                    className={inputCls}
                    placeholder="(306) 555-0100"
                    data-testid="qcca-input-phone"
                  />
                </Field>
                <Field label="Condo corporation *" testId="qcca-field-corp">
                  <input
                    required
                    value={form.condo_corporation_name}
                    onChange={setField("condo_corporation_name")}
                    className={inputCls}
                    placeholder="e.g. Condominium Corporation No. 1234"
                    data-testid="qcca-input-condo_corporation_name"
                  />
                </Field>
                <Field label="Building address *" testId="qcca-field-address" full>
                  <input
                    required
                    value={form.building_address}
                    onChange={setField("building_address")}
                    className={inputCls}
                    placeholder="123 Victoria Ave, Regina, SK"
                    data-testid="qcca-input-building_address"
                  />
                </Field>
                <Field label="Board position *" testId="qcca-field-position">
                  <div className="relative">
                    <select
                      required
                      value={form.board_position}
                      onChange={setField("board_position")}
                      className={`${inputCls} appearance-none pr-10`}
                      data-testid="qcca-input-board_position"
                    >
                      <option value="" className="bg-black">Select your position</option>
                      {BOARD_POSITIONS.map((p) => (
                        <option key={p.value} value={p.value} className="bg-black">
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </Field>
                <Field label="Years on board (optional)" testId="qcca-field-years">
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={form.years_on_board}
                    onChange={setField("years_on_board")}
                    className={inputCls}
                    placeholder="e.g. 3"
                    data-testid="qcca-input-years_on_board"
                  />
                </Field>
              </div>

              <Field label="Why do you want to join QCCA? (optional)" testId="qcca-field-message">
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={setField("message")}
                  className={`${inputCls} resize-none`}
                  placeholder="What issues are you hoping QCCA helps your board tackle?"
                  data-testid="qcca-input-message"
                />
              </Field>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">
                  AGM minutes or signature page *
                </label>
                <label
                  htmlFor="qcca-agm-upload"
                  className="flex flex-col sm:flex-row items-center gap-4 border border-dashed border-[#1a1a1a] hover:border-[#C5A52A]/60 p-6 cursor-pointer transition-colors"
                  data-testid="qcca-field-agm"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#C5A52A]/40 text-[#C5A52A]">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="font-headline text-lg text-white">
                      {agmFile ? agmFile.name : "Upload verification document"}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500 mt-1">
                      {agmFile
                        ? `${(agmFile.size / 1024 / 1024).toFixed(2)} MB · Click to replace`
                        : "PDF, PNG, JPG, WEBP · max 15 MB"}
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    id="qcca-agm-upload"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                    className="hidden"
                    onChange={onFile}
                    data-testid="qcca-input-agm_document"
                  />
                </label>
              </div>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="qcca-field-terms">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={setField("terms_accepted")}
                  className="mt-1 w-4 h-4 accent-[#C5A52A]"
                  data-testid="qcca-input-terms_accepted"
                />
                <span className="text-sm text-gray-400 leading-relaxed">
                  I confirm that I am currently an elected director of the condominium corporation listed above, and I accept the QCCA member verification and code of conduct.
                </span>
              </label>

              <button
                type="submit"
                disabled={!canSubmit}
                className="qcca-gold-glow w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
                data-testid="qcca-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit application"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section id="faq" className="py-24 lg:py-32 border-b border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">04 · FAQ</div>
            <h2 className="mt-4 font-headline text-4xl lg:text-5xl text-white">
              Questions, <span className="italic text-[#C5A52A]">answered.</span>
            </h2>
          </div>
          <div className="divide-y divide-[#1a1a1a] border-y border-[#1a1a1a]">
            {FAQS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                className="w-full text-left py-6 group"
                data-testid={`qcca-faq-${f.id}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-headline text-xl lg:text-2xl text-white group-hover:text-[#C5A52A] transition-colors">
                    {f.q}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-[#C5A52A] transition-transform shrink-0 mt-1 ${openFaq === f.id ? "rotate-180" : ""}`}
                  />
                </div>
                <div
                  className={`grid transition-all duration-300 ${openFaq === f.id ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="text-gray-400 leading-relaxed pr-8">{f.a}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CLOSING QUOTE ===================== */}
      <section className="py-24 lg:py-32 bg-[#050505] border-b border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="w-8 h-8 text-[#C5A52A] mx-auto" />
          <blockquote className="mt-8 font-headline text-3xl sm:text-4xl lg:text-5xl text-white italic leading-tight">
            “When we govern ourselves, the city takes notice.”
          </blockquote>
          <div className="mt-8 qcca-rule mx-auto max-w-xs" />
          <p className="mt-8 max-w-2xl mx-auto text-gray-400 leading-relaxed">
            QCCA is dedicated to the stewardship of Regina's housing, the rights of condo owners, and the professionalization of condo governance. Join us and help ensure condo living in Regina is defined by transparency and autonomy.
          </p>
          <button
            onClick={scrollToApply}
            className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white transition-colors"
            data-testid="qcca-closing-cta"
          >
            Claim your seat <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="py-14 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="QCCA" className="h-9 w-9 rounded-full" />
            <div>
              <div className="font-headline text-white">Queen City Condo Association</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 mt-0.5">
                Regina, Saskatchewan · Non-partisan · Board-only
              </div>
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
            © {new Date().getFullYear()} QCCA · A CYQR.ca initiative
          </div>
        </div>
      </footer>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors";

function Field({ label, children, testId, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testId}>
      <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
